import { format } from 'date-fns';
import React from 'react';
import { Loader } from 'react-feather';

import { UserPendingTableProps } from './types/userTypes';

const getStatusStyle = (status: string) => {
  const baseClasses =
    'flex py-1.5 w-20 items-center justify-center rounded-full text-xs font-medium tracking-wide';

  switch (status) {
    case 'PENDING':
      return `${baseClasses} bg-[#f2f2f2] text-[#222]`;
    case 'APPROVED':
      return `${baseClasses} bg-[#ededfc] text-[#6b6be6]`;
    case 'REJECTED':
      return `${baseClasses} bg-[#ffecec] text-[#8f1a1a]`;
    default:
      return baseClasses;
  }
};

const UserPendingTable: React.FC<UserPendingTableProps> = ({
  pendingUsers,
  onRowClick,
  isLoading = false,
  hasError = false,
  isEmpty = false,
  error = null,
}) => {
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center border border-[#dedede] bg-white">
        <div className="flex flex-col items-center gap-2">
          <Loader size={24} className="animate-spin text-gray-500" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex h-64 w-full items-center justify-center border border-[#dedede] bg-white">
        <div className="text-center">
          <p className="text-sm text-red-600">
            {error?.message || 'An error occurred while loading data.'}
          </p>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex h-64 w-full items-center justify-center border border-[#dedede] bg-white">
        <div className="text-center">
          <p className="text-sm text-gray-500">No data found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[88%] w-full overflow-x-auto border border-t-0 border-[#dedede]">
      <table
        className="w-full min-w-[1200px] table-fixed border-collapse"
        aria-label="pending users table"
      >
        <thead>
          <tr>
            <th className="sticky top-0 h-9 w-[120px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Requested At
            </th>
            <th className="sticky top-0 h-9 w-[120px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Approved At
            </th>
            <th className="sticky top-0 h-9 w-[160px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Company
            </th>
            <th className="sticky top-0 h-9 w-[180px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Business Registration Number
            </th>
            <th className="sticky top-0 h-9 w-[200px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Email
            </th>
            <th className="sticky top-0 h-9 w-[100px] border-b border-[#dedede] bg-[#efefef] px-3 py-2.5 text-left text-sm font-medium text-[#707070]">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {pendingUsers.map((user) => (
            <tr
              key={user.uuid}
              onClick={() => onRowClick(user)}
              className="cursor-pointer hover:bg-gray-50"
              tabIndex={0}
              role="button"
              aria-label={`${user.companyName} details`}
            >
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                {user.createdAt && !isNaN(new Date(user.createdAt).getTime())
                  ? format(new Date(user.createdAt), 'yyyy.MM.dd HH:mm')
                  : '-'}
              </td>
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                {user.approvedAt && !isNaN(new Date(user.approvedAt).getTime())
                  ? format(new Date(user.approvedAt), 'yyyy.MM.dd HH:mm')
                  : '-'}
              </td>
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                <div className="truncate" title={user.companyName}>
                  {user.companyName}
                </div>
              </td>
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                {user.businessRegistrationNumber}
              </td>
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                <div className="truncate" title={user.email}>
                  {user.email}
                </div>
              </td>
              <td className="h-12 border-b border-[#dedede] bg-white px-3 py-2 align-middle text-sm font-normal text-[#1d1d1d]">
                <div className={getStatusStyle(user.approvalStatus)}>
                  {user.approvalStatus}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserPendingTable;
